import { motion } from "framer-motion";
import { ServiceCard } from "@/components/ServiceCard";
import { useLanguage } from "@/contexts/LanguageContext";
import { HECHICERIA_TOPICS } from "@/data/services";
import { useCatalog } from "@/hooks/useCatalog";
import { useSiteContent } from "@/hooks/useSiteContent";
import { useAuth } from "@/contexts/AuthContext";
import { Link } from "react-router-dom";
import * as Icons from "lucide-react";

export const ReadingsSection = () => {
  const { t } = useLanguage();
  const { get } = useSiteContent();
  const { visible } = useCatalog();
  const readings = visible.filter((s) => s.category === "lectura");

  return (
    <section
      id="lecturas"
      className="relative py-24 lg:py-32 px-6 lg:px-12"
      data-testid="readings-section"
    >
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 text-[#d4af37] mb-4">
            <div className="w-12 h-px bg-[#d4af37]/40" />
            <span className="uppercase tracking-[0.3em] text-xs">{t("nav.readings")}</span>
            <div className="w-12 h-px bg-[#d4af37]/40" />
          </div>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white mb-4">
            {get("readings.title", t("sections.readingsTitle"))}
          </h2>
          <p className="text-neutral-400 text-lg max-w-xl mx-auto italic">
            {t("sections.readingsSubtitle")}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {readings.map((s, i) => (
            <ServiceCard service={s} key={s.id} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

export const WitchcraftSection = () => {
  const { lang, t } = useLanguage();
  const { get } = useSiteContent();
  const { visible } = useCatalog();
  const works = visible.filter((s) => s.category === "hechiceria");

  return (
    <section
      id="hechiceria"
      className="relative py-24 lg:py-32 px-6 lg:px-12 overflow-hidden"
      data-testid="witchcraft-section"
    >
      {/* atmospheric bg */}
      <div
        className="absolute inset-0 opacity-20 bg-cover bg-center pointer-events-none"
        style={{
          backgroundImage:
            "url('https://images.pexels.com/photos/9694697/pexels-photo-9694697.jpeg')",
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black via-black/70 to-black" />

      <div className="relative z-10 max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 text-[#d4af37] mb-4">
            <div className="w-12 h-px bg-[#d4af37]/40" />
            <span className="uppercase tracking-[0.3em] text-xs">{t("nav.witchcraft")}</span>
            <div className="w-12 h-px bg-[#d4af37]/40" />
          </div>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white mb-4">
            {get("witchcraft.title", t("sections.witchcraftTitle"))}
          </h2>
          <p className="text-neutral-400 text-lg max-w-2xl mx-auto italic">
            {t("sections.witchcraftSubtitle")}
          </p>
        </motion.div>

        {/* Topics */}
        <div className="flex flex-wrap justify-center gap-3 mb-16">
          {HECHICERIA_TOPICS[lang].map((topic) => {
            const Icon = Icons[topic.icon] || Icons.Sparkles;
            return (
              <div
                key={topic.label}
                data-testid={`topic-${topic.icon}`}
                className="flex items-center gap-2 px-4 py-2 rounded-full border border-[#d4af37]/30 bg-black/40 text-sm text-neutral-200 hover:border-[#d4af37] transition-colors"
              >
                <Icon className="w-3.5 h-3.5 text-[#d4af37]" />
                {topic.label}
              </div>
            );
          })}
        </div>

        {/* Bonus banner */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          className="glass-dark border border-[#d4af37]/40 rounded-2xl p-6 mb-12 text-center"
        >
          <div className="text-xs uppercase tracking-[0.3em] text-[#d4af37] mb-2">
            {t("sections.bonusTitle")}
          </div>
          <p className="text-white font-serif text-xl lg:text-2xl">
            {t("sections.bonusText")}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {works.map((s, i) => (
            <ServiceCard service={s} key={s.id} index={i} />
          ))}
        </div>

        <p className="text-center text-xs text-neutral-500 italic mt-8">
          {lang === "es"
            ? "Los precios pueden variar según el tiempo de resultados, duración del efecto y nivel del trabajo espiritual. Los trabajos no incluyen amuletos."
            : "Prices may vary based on result time, effect duration and level of spiritual work. Works do not include amulets."}
        </p>
      </div>
    </section>
  );
};

/* -------------------------------------------------------------------------- */
/* PACTS SECTION — Pacto de Luz + Pacto Satánico                              */
/* -------------------------------------------------------------------------- */
export const PactsSection = () => {
  const { lang } = useLanguage();
  const { get } = useSiteContent();
  const { visible } = useCatalog();
  const pacts = visible.filter((s) => s.category === "pacto");
  if (pacts.length === 0) return null;
  return (
    <section
      id="pactos"
      className="relative py-24 lg:py-28 px-6 lg:px-12 overflow-hidden"
      data-testid="pacts-section"
    >
      <div className="absolute inset-0 bg-gradient-to-b from-black via-[#0a0510] to-black pointer-events-none" />
      <div
        className="absolute inset-0 opacity-10 bg-cover bg-center pointer-events-none"
        style={{ backgroundImage: "url('https://images.pexels.com/photos/8459503/pexels-photo-8459503.jpeg')" }}
      />
      <div className="relative z-10 max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 text-[#d4af37] mb-4">
            <div className="w-12 h-px bg-[#d4af37]/40" />
            <span className="uppercase tracking-[0.3em] text-xs">
              {lang === "es" ? "Pactos Espirituales" : "Spiritual Pacts"}
            </span>
            <div className="w-12 h-px bg-[#d4af37]/40" />
          </div>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white mb-4">
            {get("pacts.title", lang === "es" ? "El siguiente nivel" : "The next level")}
          </h2>
          <p className="text-neutral-400 text-lg max-w-2xl mx-auto italic">
            {lang === "es"
              ? "Sellos espirituales seguros para quienes buscan transformación permanente y protección absoluta."
              : "Safe spiritual seals for those seeking permanent transformation and absolute protection."}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {pacts.map((s, i) => (
            <ServiceCard service={s} key={s.id} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

/* -------------------------------------------------------------------------- */
/* EXTRA CATEGORIES — categorías creadas desde el panel / asistente IA        */
/* -------------------------------------------------------------------------- */
export const ExtraCategoriesSection = () => {
  const { lang } = useLanguage();
  const { visible, extraCategories } = useCatalog();
  if (extraCategories.length === 0) return null;
  return (
    <>
      {extraCategories.map((cat) => {
        const items = visible.filter((s) => s.category === cat.id);
        return (
          <section
            key={cat.id}
            id={`cat-${cat.id}`}
            className="relative py-24 lg:py-28 px-6 lg:px-12"
            data-testid={`extra-category-section-${cat.id}`}
          >
            <div className="max-w-7xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
                className="text-center mb-12"
              >
                <div className="inline-flex items-center gap-2 text-[#d4af37] mb-4">
                  <div className="w-12 h-px bg-[#d4af37]/40" />
                  <span className="uppercase tracking-[0.3em] text-xs">{lang === "es" ? "Catálogo" : "Catalog"}</span>
                  <div className="w-12 h-px bg-[#d4af37]/40" />
                </div>
                <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white mb-4">{cat.label[lang]}</h2>
              </motion.div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {items.map((s, i) => (
                  <ServiceCard service={s} key={s.id} index={i} />
                ))}
              </div>
            </div>
          </section>
        );
      })}
    </>
  );
};

/* -------------------------------------------------------------------------- */
/* MEMBERS GATE — servicios, precios y pagos sólo para usuarios registrados    */
/* -------------------------------------------------------------------------- */
export const ServicesLockedSection = () => {
  const { lang } = useLanguage();
  const es = lang === "es";
  return (
    <section id="servicios" className="relative py-24 lg:py-28 px-6 lg:px-12" data-testid="services-locked-section">
      <div className="max-w-3xl mx-auto text-center rounded-3xl border border-[#d4af37]/30 bg-gradient-to-b from-[#d4af37]/10 to-transparent p-6 sm:p-10 lg:p-14">
        <div className="w-14 h-14 rounded-full bg-[#d4af37]/15 border border-[#d4af37]/40 flex items-center justify-center mx-auto mb-5">
          <Icons.Lock className="w-6 h-6 text-[#d4af37]" />
        </div>
        <span className="uppercase tracking-[0.3em] text-xs text-[#d4af37]">{es ? "Exclusivo para miembros" : "Members only"}</span>
        <h2 className="font-serif text-4xl sm:text-5xl text-white mt-3 mb-4">
          {es ? "Regístrate para ver servicios y precios" : "Sign up to see services and prices"}
        </h2>
        <p className="text-base md:text-lg text-neutral-300 max-w-xl mx-auto mb-8">
          {es
            ? "Lecturas, trabajos espirituales, pactos, precios en MXN y USD y todos los métodos de pago están disponibles al crear tu cuenta gratis. Toma menos de un minuto."
            : "Readings, spiritual works, pacts, prices in MXN and USD and all payment methods are available once you create your free account. It takes less than a minute."}
        </p>
        <div className="flex flex-col sm:flex-row items-center gap-3 justify-center">
          <Link
            to="/registro"
            data-testid="services-locked-register-btn"
            className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-[#d4af37] text-black font-bold text-xs uppercase tracking-[0.2em] hover:bg-[#e6c35a] transition-colors"
          >
            <Icons.UserPlus className="w-4 h-4" /> {es ? "Registrarme gratis" : "Sign up free"}
          </Link>
          <Link
            to="/ingresar"
            data-testid="services-locked-login-btn"
            className="inline-flex items-center gap-2 px-7 py-3 rounded-full border border-[#d4af37]/50 text-[#d4af37] text-xs uppercase tracking-[0.2em] hover:bg-[#d4af37]/10 transition-colors"
          >
            <Icons.LogIn className="w-4 h-4" /> {es ? "Ya tengo cuenta" : "I have an account"}
          </Link>
        </div>
      </div>
    </section>
  );
};

export const MembersServices = () => (
  <>
    <ReadingsSection />
    <WitchcraftSection />
    <PactsSection />
    <ExtraCategoriesSection />
  </>
);

/* Testimonios: sólo usuarios registrados y logueados */
export const MembersTestimonials = ({ children }) => {
  const { user, loading } = useAuth();
  const { lang } = useLanguage();
  if (loading) return null;
  if (user) return children;
  const es = lang === "es";
  return (
    <section id="testimonios" className="relative py-24 px-6 lg:px-12" data-testid="testimonials-locked-section">
      <div className="max-w-3xl mx-auto text-center rounded-3xl border border-[#d4af37]/30 bg-gradient-to-b from-[#d4af37]/10 to-transparent p-6 sm:p-10 lg:p-14">
        <div className="w-14 h-14 rounded-full bg-[#d4af37]/15 border border-[#d4af37]/40 flex items-center justify-center mx-auto mb-5">
          <Icons.MessageSquareQuote className="w-6 h-6 text-[#d4af37]" />
        </div>
        <span className="uppercase tracking-[0.3em] text-xs text-[#d4af37]">{es ? "Testimonios" : "Testimonials"}</span>
        <h2 className="font-serif text-4xl sm:text-5xl text-white mt-3 mb-4">
          {es ? "Regístrate para leer los testimonios" : "Sign up to read the testimonials"}
        </h2>
        <p className="text-base md:text-lg text-neutral-300 max-w-xl mx-auto mb-8">
          {es
            ? "Las experiencias reales de mis clientes están disponibles para usuarios registrados. Crear tu cuenta es gratis y toma menos de un minuto."
            : "Real experiences from my clients are available to registered users. Creating your account is free and takes less than a minute."}
        </p>
        <div className="flex flex-col sm:flex-row items-center gap-3 justify-center">
          <Link to="/registro?next=/testimonios" data-testid="testimonials-locked-register-btn" className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-[#d4af37] text-black font-bold text-xs uppercase tracking-[0.2em] hover:bg-[#e6c35a] transition-colors">
            <Icons.UserPlus className="w-4 h-4" /> {es ? "Registrarme gratis" : "Sign up free"}
          </Link>
          <Link to="/ingresar?next=/testimonios" data-testid="testimonials-locked-login-btn" className="inline-flex items-center gap-2 px-7 py-3 rounded-full border border-[#d4af37]/50 text-[#d4af37] text-xs uppercase tracking-[0.2em] hover:bg-[#d4af37]/10 transition-colors">
            <Icons.LogIn className="w-4 h-4" /> {es ? "Ya tengo cuenta" : "I have an account"}
          </Link>
        </div>
      </div>
    </section>
  );
};
